import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, User } from "lucide-react";
import { api } from "@/lib/api";
import { SectionKicker, FadeIn } from "@/components/shared/SectionBits";

export default function PostDetailPage({ type }) {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        setStatus("loading");
        api.get(`/posts/${slug}`)
            .then((r) => { setData(r.data); setStatus("ok"); })
            .catch(() => setStatus("notfound"));
    }, [slug]);

    const backTo = type === "news" ? "/news" : "/blog";
    const backLabel = type === "news" ? "All news" : "All insights";
    const linkBase = type === "news" ? "/news" : "/blog";

    if (status === "loading") return <div className="dr-container py-32 text-dr-dark/60">Loading…</div>;
    if (status === "notfound" || !data) return (
        <div className="dr-container py-32">
            <p className="text-dr-dark/60">Article not found.</p>
            <Link to={backTo} className="text-dr-navy underline">{backLabel}</Link>
        </div>
    );

    const { post, related } = data;
    const formatted = new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

    return (
        <div data-testid={`post-detail-${post.slug}`}>
            {/* Cover */}
            <section className="relative min-h-[55vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-dr-navy/95 via-dr-navy/70 to-dr-navy/20" />
                </div>
                <div className="relative dr-container w-full pt-32 pb-14">
                    <Link to={backTo} data-testid="post-back-link" className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> {backLabel}
                    </Link>
                    <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium mt-8">{post.category}</p>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-5 max-w-4xl"
                    >
                        {post.title}
                    </motion.h1>
                    <div className="flex flex-wrap items-center gap-6 mt-8 text-white/80 text-sm">
                        <span className="inline-flex items-center gap-2"><User className="w-4 h-4" />{post.author}</span>
                        <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" />{post.read_minutes} min read</span>
                        <span>{formatted}</span>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="dr-container py-20">
                <div className="grid lg:grid-cols-12 gap-14">
                    <article className="lg:col-span-8 dr-prose">
                        <p className="text-xl text-dr-navy font-display font-medium leading-relaxed mb-10">
                            {post.excerpt}
                        </p>
                        {post.content.split("\n").map((raw, i) => {
                            const t = raw.trim();
                            if (!t) return null;
                            if (t.startsWith("### ")) return <h3 key={i}>{t.slice(4)}</h3>;
                            if (t.startsWith("## ")) return <h2 key={i}>{t.slice(3)}</h2>;
                            if (t.startsWith("- ")) return <p key={i} className="dr-bullet">{t.slice(2)}</p>;
                            return <p key={i}>{t}</p>;
                        })}
                    </article>

                    <aside className="lg:col-span-4">
                        <div className="lg:sticky lg:top-28 border border-dr-silver rounded-2xl p-7 bg-dr-grey">
                            <p className="text-xs tracking-[0.28em] uppercase text-dr-blue font-medium">Contact</p>
                            <h3 className="font-display text-2xl font-semibold text-dr-navy mt-3 leading-tight">
                                Discuss this in confidence with a senior consultant.
                            </h3>
                            <Link to="/contact" data-testid="post-cta-contact" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-dr-navy text-white text-sm font-medium hover:bg-dr-blue transition-colors">
                                Speak to us <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Related */}
            {related?.length > 0 && (
                <section className="bg-dr-grey py-24">
                    <div className="dr-container">
                        <SectionKicker>Related</SectionKicker>
                        <h3 className="font-display text-3xl md:text-4xl font-bold text-dr-navy mb-10">More on {post.category}</h3>
                        <div className="grid md:grid-cols-3 gap-6">
                            {related.map((p, i) => (
                                <FadeIn key={p.slug} delay={i * 0.06}>
                                    <Link
                                        to={`${linkBase}/${p.slug}`}
                                        data-testid={`related-post-${p.slug}`}
                                        className="group block h-full bg-white border border-dr-silver rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(8,41,74,0.10)] transition-all duration-500"
                                    >
                                        {p.cover_image && (
                                            <div className="aspect-[16/10] overflow-hidden">
                                                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                            </div>
                                        )}
                                        <div className="p-6">
                                            <p className="text-[11px] tracking-[0.24em] uppercase text-dr-blue font-medium">{p.category}</p>
                                            <h4 className="font-display text-lg font-semibold text-dr-navy mt-2 leading-snug">{p.title}</h4>
                                        </div>
                                    </Link>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
